import { FC, useMemo } from 'react';
import { Control, useWatch } from 'react-hook-form';

import { AppFormattedMessage } from '../../../../../../components/app-formatted-message/index';
import { SummaryItem } from '../../../../../../components/summary-list/index';
import { useLocale } from '../../../../../../hooks/index';
import {
  getDocumentCategoryDisplayLabel,
  getDocumentSubCategoryDisplayLabel,
} from '../../../../constants/document-category.constant';
import {
  expirationPeriodLabelKey,
  signingModeLabelKey,
} from '../../../../templates/enums/template.const';
import { downloadFile } from '../../../../templates/utils/download-file.util';
import { useLinkedTargetOptions } from '../../../hooks/use-linked-target-options.hook';
import { renderLinkedEventValue } from '../../../utils/render-linked-event-value.util';
import { resolveLinkedEventDisplayLabel } from '../../../utils/resolve-linked-event-display-label.util';
import {
  getQuickSignStepFourFile,
  QuickSignFormSchema,
} from '../../../validation/quick-sign.validation';

export type QuickSignStepFiveProps = {
  companyId: string;
  control: Control<QuickSignFormSchema>;
};

export const QuickSignStepFive: FC<QuickSignStepFiveProps> = ({ companyId, control }) => {
  const { messagesLocale } = useLocale();

  const stepOne = useWatch({ control, name: 'stepOne' });
  const stepTwo = useWatch({ control, name: 'stepTwo' });
  const stepThree = useWatch({ control, name: 'stepThree' });
  const stepFour = useWatch({ control, name: 'stepFour' });

  const { options } = useLinkedTargetOptions(companyId, stepOne?.subCategory);

  const linkedEventLabel = useMemo(
    () =>
      resolveLinkedEventDisplayLabel({
        linkedTargetId: stepOne?.linkedTargetId,
        options,
      }),
    [options, stepOne?.linkedTargetId],
  );

  const signatoryLines = (stepThree?.signatorySlots ?? [])
    .map((slot, index) => {
      const name = slot.stakeholder?.fullName;
      if (!name) return null;
      return `${index + 1}. ${name}`;
    })
    .filter((line): line is string => line != null);

  const uploadedFile = getQuickSignStepFourFile(stepFour?.file);

  const handleDownloadFile = () => {
    if (uploadedFile) {
      downloadFile(uploadedFile);
    }
  };

  return (
    <div className="flex h-full flex-col bg-gray-100">
      <div className="flex flex-1 flex-col gap-4 overflow-y-auto rounded-b-xl border-b border-gray-300 bg-white px-6 pb-4 pt-6">
        <section className="flex flex-col gap-2 rounded-lg bg-gray-50 pt-3">
          <span className="px-4 text-sm font-semibold text-gray-700">
            <AppFormattedMessage id="DOCUMENT" />
          </span>
          <div className="flex flex-col divide-y divide-gray-100 rounded-b-lg border border-gray-100 bg-white px-4">
            <SummaryItem
              className="py-3"
              title={<AppFormattedMessage id="DOCUMENT_NAME" />}
              titleClassName="w-fit"
              titleWrapperClassName="w-[120px]"
              value={stepOne?.name}
            />
            <SummaryItem
              className="py-3"
              title={<AppFormattedMessage id="CATEGORY" />}
              titleClassName="w-fit"
              titleWrapperClassName="w-[120px]"
              value={getDocumentCategoryDisplayLabel(stepOne?.category, messagesLocale) ?? '-'}
            />
            <SummaryItem
              className="py-3"
              title={<AppFormattedMessage id="SUB_CATEGORY" />}
              titleClassName="w-fit"
              titleWrapperClassName="w-[120px]"
              value={getDocumentSubCategoryDisplayLabel(stepOne?.subCategory, messagesLocale)}
            />
            {linkedEventLabel && (
              <SummaryItem
                className="py-3"
                title={<AppFormattedMessage id="LINKED_EVENT" />}
                titleClassName="w-fit"
                titleWrapperClassName="w-[120px]"
                value={renderLinkedEventValue(linkedEventLabel)}
                valueClassName="min-w-0 max-w-[250px] overflow-hidden"
              />
            )}
          </div>
        </section>

        <div className="flex flex-col divide-y divide-gray-100 rounded-lg border border-gray-100 bg-white px-4">
          <SummaryItem
            className="py-3"
            title={<AppFormattedMessage id="SIGNING_MODE" />}
            titleClassName="w-fit"
            titleWrapperClassName="w-[120px]"
            value={
              stepTwo?.signingMode ? messagesLocale[signingModeLabelKey[stepTwo.signingMode]] : '-'
            }
          />
          {stepTwo?.expireDocument && stepTwo.expirationPeriod && (
            <SummaryItem
              className="py-3"
              title={<AppFormattedMessage id="EXPIRATION_PERIOD" />}
              titleClassName="w-fit"
              titleWrapperClassName="w-[120px]"
              value={
                <AppFormattedMessage id={expirationPeriodLabelKey[stepTwo.expirationPeriod]} />
              }
            />
          )}
          <SummaryItem
            className="py-3"
            title={<AppFormattedMessage id="SIGNERS" />}
            titleClassName="w-fit"
            titleWrapperClassName="w-[120px]"
            value={
              signatoryLines.length > 0 ? (
                <ul className="list-none pl-0 text-left">
                  {signatoryLines.map((line) => (
                    <li className="text-xs text-gray-700" key={line}>
                      {line}
                    </li>
                  ))}
                </ul>
              ) : (
                '-'
              )
            }
            valueClassName="max-w-none whitespace-normal"
          />
        </div>

        <div className="flex flex-col rounded-lg border border-gray-100 bg-white px-4">
          <SummaryItem
            className="py-3"
            title={<AppFormattedMessage id="DOCUMENT" />}
            titleClassName="w-fit"
            titleWrapperClassName="w-[120px]"
            value={
              uploadedFile ? (
                <button
                  className="block w-full max-w-full cursor-pointer truncate text-left text-brand-700 underline"
                  onClick={handleDownloadFile}
                  title={uploadedFile.name}
                  type="button"
                >
                  {uploadedFile.name}
                </button>
              ) : (
                '-'
              )
            }
            valueClassName="max-w-none"
          />
        </div>
      </div>
    </div>
  );
};
